/**
 * Custom element for accessible video banner controls
 * Handles play/pause functionality with proper ARIA attributes
 */
class VideoBannerControls extends HTMLElement {
  constructor() {
    super();
    this.desktopVideo = null;
    this.mobileVideo = null;
    this.desktopControls = null;
    this.mobileControls = null;
    this.isPlaying = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Find video elements
    this.desktopVideo = this.querySelector('#banner-desktop-video--' + this.dataset.sectionId);
    this.mobileVideo = this.querySelector('#banner-mobile-video--' + this.dataset.sectionId);

    // Find control groups
    this.desktopControls = this.querySelector('.desktop-video-controls, .video-controls:not(.mobile-video-controls)');
    this.mobileControls = this.querySelector('.mobile-video-controls');

    if (!this.desktopVideo && !this.mobileVideo) {
      console.warn('VideoBannerControls: No video elements found');
      return;
    }

    this.setupControls();
    this.addEventListeners();
    this.checkInitialState();
  }

  setupControls() {
    // Setup desktop controls
    if (this.desktopControls && this.desktopVideo) {
      this.setupControlButtons(this.desktopControls, this.desktopVideo);
    }

    // Setup mobile controls
    if (this.mobileControls && this.mobileVideo) {
      this.setupControlButtons(this.mobileControls, this.mobileVideo);
    }
  }

  setupControlButtons(controlGroup, video) {
    const button = controlGroup.querySelector('.video-control-button');
    
    if (!button) return;
    
    // Add click handler
    button.addEventListener('click', (e) => {
      e.preventDefault();
      this.togglePlayPause(video, button);
    });
  }

  addEventListeners() {
    // Listen to video play/pause events to sync state
    if (this.desktopVideo) {
      this.desktopVideo.addEventListener('play', () => this.handleVideoPlay(this.desktopControls));
      this.desktopVideo.addEventListener('pause', () => this.handleVideoPause(this.desktopControls));
    }

    if (this.mobileVideo) {
      this.mobileVideo.addEventListener('play', () => this.handleVideoPlay(this.mobileControls));
      this.mobileVideo.addEventListener('pause', () => this.handleVideoPause(this.mobileControls));
    }
  }

  checkInitialState() {
    // Check if videos are autoplaying and update button state
    // Small delay to ensure video metadata is loaded
    setTimeout(() => {
      if (this.desktopVideo) {
        const isPlaying = !this.desktopVideo.paused;
        if (isPlaying) {
          this.handleVideoPlay(this.desktopControls);
        } else {
          this.handleVideoPause(this.desktopControls);
        }
      }

      if (this.mobileVideo) {
        const isPlaying = !this.mobileVideo.paused;
        if (isPlaying) {
          this.handleVideoPlay(this.mobileControls);
        } else {
          this.handleVideoPause(this.mobileControls);
        }
      }
    }, 100);
  }

  togglePlayPause(video, button) {
    if (!video) return;

    if (video.paused) {
      video.play()
        .then(() => {
          this.updateButtonState(button, 'playing');
        })
        .catch(err => {
          console.error('Error playing video:', err);
        });
    } else {
      video.pause();
      this.updateButtonState(button, 'paused');
    }
  }

  updateButtonState(button, state) {
    if (!button) return;

    const playText = button.querySelector('.play-text');
    const pauseText = button.querySelector('.pause-text');

    if (state === 'playing') {
      // Video is playing, so button should show "Pause"
      button.setAttribute('data-state', 'playing');
      button.setAttribute('aria-label', 'Pause video');
      button.setAttribute('aria-pressed', 'false'); // Not pressed because it can perform action
      
      if (playText) playText.hidden = true;
      if (pauseText) pauseText.hidden = false;
    } else {
      // Video is paused, so button should show "Play"
      button.setAttribute('data-state', 'paused');
      button.setAttribute('aria-label', 'Play video');
      button.setAttribute('aria-pressed', 'false');
      
      if (playText) playText.hidden = false;
      if (pauseText) pauseText.hidden = true;
    }
  }

  handleVideoPlay(controlGroup) {
    if (!controlGroup) return;

    this.isPlaying = true;
    const button = controlGroup.querySelector('.video-control-button');
    
    if (button) {
      this.updateButtonState(button, 'playing');
    }

    // Dispatch custom event for other components
    this.dispatchEvent(new CustomEvent('video-playing', { 
      bubbles: true, 
      detail: { controlGroup } 
    }));
  }

  handleVideoPause(controlGroup) {
    if (!controlGroup) return;

    this.isPlaying = false;
    const button = controlGroup.querySelector('.video-control-button');
    
    if (button) {
      this.updateButtonState(button, 'paused');
    }

    // Dispatch custom event for other components
    this.dispatchEvent(new CustomEvent('video-paused', { 
      bubbles: true, 
      detail: { controlGroup } 
    }));
  }

  // Public API methods
  play() {
    const activeVideo = this.getActiveVideo();
    if (activeVideo && activeVideo.paused) {
      activeVideo.play();
    }
  }

  pause() {
    const activeVideo = this.getActiveVideo();
    if (activeVideo && !activeVideo.paused) {
      activeVideo.pause();
    }
  }

  getActiveVideo() {
    // Return the visible video based on viewport
    if (this.mobileVideo && window.innerWidth < 750) {
      return this.mobileVideo;
    }
    return this.desktopVideo;
  }
}

// Register the custom element
if (!customElements.get('video-banner-controls')) {
  customElements.define('video-banner-controls', VideoBannerControls);
}

export default VideoBannerControls;

